// src/native.js — the platform bridge between Lift Log's web code and the
// native Android / iOS shell (Capacitor). Every export works on the web too:
// on the web it either falls back to the browser API or is a harmless no-op,
// so callers never branch on the platform themselves.
//
//   isNative(), platform()
//   Rest alerts .......... scheduleRestAlert({ at, title, body }), cancelRestAlert()
//                          (exact alarm, shows on the lock screen with the app
//                          backgrounded or the phone locked)
//   Training reminders ... scheduleTrainingReminders([...]), cancelTrainingReminders(),
//                          remindersSupported()
//   Notifications ........ notificationStatus(), requestNotifications(),
//                          exactAlarmStatus(), openExactAlarmSettings(),
//                          openNotificationSettings(), sendTestRestAlert()
//   Haptics .............. nativeHaptic(pattern)  (timers.jsx tryVibrate routes here)
//   Health Connect ....... health* (opt-in; write finished workouts, read body weight)
//   Share / files ........ shareFile(blob, name), plus navigator.share / download
//                          shims so existing export + share code works in the app
//   Wear OS watch ........ watchSupported(), watchStatus(), useWatchLink() (live
//                          workout screen); state publishing + watch commands
//   useNativeBridge(app) . mounted once (shell ThemeInjector): Android back
//                          button, deep links, status-bar theme, keep-awake
//                          during a workout, Health Connect sync, watch sync.

import React from 'react';
import { Capacitor, registerPlugin, SystemBars, SystemBarsStyle } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { KeepAwake } from '@capacitor-community/keep-awake';
import { App as CapApp } from '@capacitor/app';
import { Share } from '@capacitor/share';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { StatusBar, Style as StatusStyle } from '@capacitor/status-bar';
import { openSheetCount } from './sheet_history.js';
import { buildWatchWorkout, buildWatchSummary, buildWatchState, parseWatchCommand, withSessionRest, adjustedRest, WATCH_LIMITS } from './watch_protocol.js';
import { nextWorkout } from './progression.js';

const LiftHealth = registerPlugin('LiftHealth');
const LiftShell = registerPlugin('LiftShell');
// Theme canvas colours (tokens.jsx bg as hex) for the native window behind the WebView.
const THEME_BG = { dark: '#0E1011', light: '#ECEFF1', oled: '#000000' };

export function isNative() {
  try { return Capacitor.isNativePlatform(); } catch (_) { return false; }
}
export function platform() {
  try { return Capacitor.getPlatform(); } catch (_) { return 'web'; }
}
const isAndroid = () => platform() === 'android';

// Latest app snapshot (set by useNativeBridge) so module-level helpers can
// honour prefs without every caller passing them in.
const cur = { state: null, route: null, app: null };

const swallow = () => {};
const REST_ID = 41001;
const TEST_ID = 41002;
const REMINDER_BASE = 41100;
const REMINDER_MAX = 64;

// One native call at a time for scheduling, so a fast sequence of rest
// adjustments (+15 s, +15 s, skip) can never land out of order.
let queue = Promise.resolve();
function serial(fn) {
  const run = queue.then(fn, fn);
  queue = run.catch(swallow);
  return run;
}

// ───────────────────────────── notifications ─────────────────────────────

let channelsReady = null;
function ensureChannels() {
  if (!isAndroid()) return Promise.resolve();
  if (!channelsReady) {
    channelsReady = Promise.all([
      LocalNotifications.createChannel({
        id: 'rest', name: 'Rest timer', description: 'Tells you when rest is over, even with the phone locked',
        importance: 5, visibility: 1, vibration: true, lights: true, lightColor: '#A3E635',
      }),
      LocalNotifications.createChannel({
        id: 'reminders', name: 'Training reminders', description: 'Reminders on the days you plan to train',
        importance: 3, visibility: 1, vibration: true,
      }),
    ]).catch((e) => { channelsReady = null; throw e; });
  }
  return channelsReady.catch(swallow);
}

const mapPerm = (p) => (p === 'granted' ? 'granted' : p === 'denied' ? 'denied' : 'default');

/** 'granted' | 'denied' | 'default' | 'unsupported' */
export async function notificationStatus() {
  if (!isNative()) {
    try { return typeof Notification === 'undefined' ? 'unsupported' : Notification.permission; } catch (_) { return 'unsupported'; }
  }
  try { const r = await LocalNotifications.checkPermissions(); return setPermCache(mapPerm(r.display)); } catch (_) { return 'default'; }
}
export async function requestNotifications() {
  if (!isNative()) {
    try { return typeof Notification === 'undefined' ? 'unsupported' : await Notification.requestPermission(); } catch (_) { return 'default'; }
  }
  try { const r = await LocalNotifications.requestPermissions(); return setPermCache(mapPerm(r.display)); } catch (_) { return 'default'; }
}

let exactCache = null;
/** Android 12+: 'granted' when rest alerts can fire to the second. Elsewhere 'granted'. */
export async function exactAlarmStatus({ fresh = false } = {}) {
  if (!isAndroid()) return 'granted';
  if (exactCache && !fresh) return exactCache;
  try { const r = await LocalNotifications.checkExactNotificationSetting(); exactCache = r.exact_alarm === 'granted' ? 'granted' : 'denied'; }
  catch (_) { exactCache = 'granted'; }
  return exactCache;
}
/** Opens Android's "Alarms & reminders" page for Lift Log; resolves with the new status. */
export async function openExactAlarmSettings() {
  if (!isAndroid()) return 'granted';
  try { await LocalNotifications.changeExactNotificationSetting(); } catch (_) {}
  return exactAlarmStatus({ fresh: true });
}
/** Android: the app's notification settings page (when permission was denied). */
export async function openNotificationSettings() {
  if (!isAndroid()) return false;
  try { await LiftHealth.openAppNotificationSettings(); return true; } catch (_) { return false; }
}

/**
 * Schedule the lock-screen "rest over" alert. Replaces any earlier one.
 * Web: no-op (the in-page timer + Web Push in sync.js handle it).
 */
export function scheduleRestAlert(a, t, b) {
  // scheduleRestAlert(at, title, body) or scheduleRestAlert({ at, title, body })
  const o = typeof a === 'object' && a ? a : { at: a, title: t, body: b };
  const at = +o.at, title = o.title || 'Rest over', body = o.body || 'Time for your next set';
  if (!isNative()) return Promise.resolve(false);
  if (cur.state && cur.state.prefs && cur.state.prefs.notifRest === false) return cancelRestAlert().then(() => false);
  if (!(at > Date.now() + 800)) return Promise.resolve(false);
  return serial(async () => {
    if ((await notificationStatus()) !== 'granted') return false;
    await ensureChannels();
    const exact = (await exactAlarmStatus()) === 'granted';
    await LocalNotifications.cancel({ notifications: [{ id: REST_ID }] }).catch(swallow);
    await LocalNotifications.schedule({
      notifications: [{
        id: REST_ID, title, body,
        channelId: 'rest', smallIcon: 'ic_stat_liftlog', iconColor: '#A3E635',
        autoCancel: true,
        schedule: { at: new Date(at), allowWhileIdle: true },
        // Never let the plugin open the system settings page mid-workout: only
        // ask for an exact alarm when the user already allowed it.
        isExactNotification: exact,
        extra: { kind: 'rest' },
      }],
    });
    return true;
  }).catch(() => false);
}
export function cancelRestAlert() {
  if (!isNative()) return Promise.resolve();
  return serial(async () => {
    await LocalNotifications.cancel({ notifications: [{ id: REST_ID }] }).catch(swallow);
  });
}
/** A rest alert in `seconds` so the user can lock the phone and check it works. */
export function sendTestRestAlert(seconds = 5) {
  if (!isNative()) return Promise.resolve(false);
  return serial(async () => {
    if ((await notificationStatus()) !== 'granted' && (await requestNotifications()) !== 'granted') return false;
    await ensureChannels();
    const exact = (await exactAlarmStatus()) === 'granted';
    await LocalNotifications.schedule({
      notifications: [{
        id: TEST_ID, title: 'Rest over', body: 'Test alert · this is how every rest ends',
        channelId: 'rest', smallIcon: 'ic_stat_liftlog', iconColor: '#A3E635', autoCancel: true,
        schedule: { at: new Date(Date.now() + seconds * 1000), allowWhileIdle: true },
        isExactNotification: exact, extra: { kind: 'test' },
      }],
    });
    return true;
  }).catch(() => false);
}

// ───────────────────────────── training reminders ─────────────────────────────

export function remindersSupported() { return isNative(); }

/**
 * Weekly training reminders (replaces all earlier ones).
 * reminders: [{ weekday: 0-6 (0 = Sunday, like Date#getDay), hour: 0-23, minute: 0-59, title?, body? }]
 * → { ok, scheduled, reason? }  reason: 'web' | 'permission' | 'error'
 */
export function scheduleTrainingReminders(reminders = []) {
  if (!isNative()) return Promise.resolve({ ok: false, scheduled: 0, reason: 'web' });
  return serial(async () => {
    await cancelReminderIds();
    const list = (reminders || []).filter(r => r && r.weekday >= 0 && r.weekday <= 6 && r.hour >= 0 && r.hour <= 23).slice(0, REMINDER_MAX);
    if (!list.length) return { ok: true, scheduled: 0 };
    let perm = await notificationStatus();
    if (perm !== 'granted') perm = await requestNotifications();
    if (perm !== 'granted') return { ok: false, scheduled: 0, reason: 'permission' };
    await ensureChannels();
    await LocalNotifications.schedule({
      notifications: list.map((r, i) => ({
        id: REMINDER_BASE + i,
        title: r.title || 'Training today',
        body: r.body || 'Your next workout is ready in Lift Log.',
        channelId: 'reminders', smallIcon: 'ic_stat_liftlog', iconColor: '#A3E635', autoCancel: true,
        // Capacitor weekdays are 1 = Sunday … 7 = Saturday.
        schedule: { on: { weekday: r.weekday + 1, hour: r.hour, minute: r.minute || 0 }, allowWhileIdle: true },
        isExactNotification: false,
        extra: { kind: 'reminder', route: r.route || 'home' },
      })),
    });
    return { ok: true, scheduled: list.length };
  }).catch(() => ({ ok: false, scheduled: 0, reason: 'error' }));
}
async function cancelReminderIds() {
  const ids = Array.from({ length: REMINDER_MAX }, (_, i) => ({ id: REMINDER_BASE + i }));
  await LocalNotifications.cancel({ notifications: ids }).catch(swallow);
}
export function cancelTrainingReminders() {
  if (!isNative()) return Promise.resolve();
  return serial(cancelReminderIds);
}

// ───────────────────────────── haptics ─────────────────────────────

/**
 * Native haptics for the vibration patterns the app already uses:
 *   short tap (≤ 15 ms) → light impact (set logged, keypad)
 *   [20, 60, 30]        → success (new PR)
 *   [80, 60, 80]        → warning buzz (rest over)
 * Returns true when handled natively.
 */
export function nativeHaptic(pattern) {
  if (!isNative()) return false;
  try {
    if (Array.isArray(pattern)) {
      const [a = 0] = pattern;
      if (a >= 60) Haptics.notification({ type: NotificationType.Warning }).catch(swallow);
      else Haptics.notification({ type: NotificationType.Success }).catch(swallow);
    } else if (typeof pattern === 'number') {
      if (pattern <= 15) Haptics.impact({ style: ImpactStyle.Light }).catch(swallow);
      else if (pattern <= 40) Haptics.impact({ style: ImpactStyle.Medium }).catch(swallow);
      else Haptics.vibrate({ duration: Math.min(1000, pattern) }).catch(swallow);
    }
    return true;
  } catch (_) { return false; }
}

// ───────────────────────────── keep awake ─────────────────────────────

let wakeOn = false;
let webLock = null;
async function setKeepAwake(on) {
  if (on === wakeOn && (isNative() || !on || webLock)) return;
  wakeOn = on;
  if (isNative()) {
    try { if (on) await KeepAwake.keepAwake(); else await KeepAwake.allowSleep(); } catch (_) {}
    return;
  }
  try {
    if (on) {
      if (!webLock && navigator.wakeLock && document.visibilityState === 'visible') {
        webLock = await navigator.wakeLock.request('screen');
        webLock.addEventListener('release', () => { webLock = null; });
      }
    } else if (webLock) {
      const l = webLock; webLock = null; await l.release();
    }
  } catch (_) { webLock = null; }
}
export function keepAwakeSupported() {
  return isNative() || (typeof navigator !== 'undefined' && !!navigator.wakeLock);
}

// ───────────────────────────── share + files ─────────────────────────────

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ''));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}
const safeName = (n) => String(n || 'liftlog-file').replace(/[\\/:*?"<>|]+/g, '-').slice(0, 120);

async function writeCacheFile(blob, name) {
  const res = await Filesystem.writeFile({
    path: `exports/${Date.now()}/${safeName(name)}`, data: await blobToBase64(blob),
    directory: Directory.Cache, recursive: true,
  });
  return res.uri;
}
function abortError() {
  try { return new DOMException('Share canceled', 'AbortError'); } catch (_) { const e = new Error('Share canceled'); e.name = 'AbortError'; return e; }
}
const isCancel = (e) => /cancel/i.test(String((e && (e.message || e.errorMessage)) || e));

/**
 * Save / send a file. Native: the system share sheet (Save to Files, Drive,
 * email, …). Web: a normal download. Resolves true when handed off.
 */
export async function shareFile(blob, name, { title } = {}) {
  if (!isNative()) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    return true;
  }
  try {
    const uri = await writeCacheFile(blob, name);
    await Share.share({ title: title || name, files: [uri], dialogTitle: `Save or send ${name}` });
    return true;
  } catch (e) {
    if (isCancel(e)) return false;
    throw e;
  }
}

// Shims: the existing web code exports files with <a download> + blob URLs and
// shares with navigator.share — neither works inside an Android WebView. In the
// app, route both to the native share sheet so every export/share button works
// without touching each caller.
function installShims() {
  if (!isNative() || typeof window === 'undefined' || window.__llNativeShims) return;
  window.__llNativeShims = true;

  // No service worker in the app: the web assets ship inside the APK / IPA and
  // the WebView's asset loader does not serve workers. main.jsx still calls
  // register() on load — make that a quiet no-op instead of a failed fetch.
  try {
    if (navigator.serviceWorker) {
      Object.defineProperty(navigator.serviceWorker, 'register', { value: () => new Promise(() => {}), configurable: true });
    }
  } catch (_) {}

  // Remember blobs behind object URLs (callers revoke them right after click()).
  const blobs = new Map();
  const create = URL.createObjectURL.bind(URL);
  const revoke = URL.revokeObjectURL.bind(URL);
  URL.createObjectURL = (obj) => {
    const url = create(obj);
    if (obj instanceof Blob) blobs.set(url, obj);
    return url;
  };
  URL.revokeObjectURL = (url) => {
    setTimeout(() => { blobs.delete(url); revoke(url); }, 60000);
  };

  const handleDownload = (a) => {
    const href = a.getAttribute('href') || '';
    if (!a.hasAttribute('download') || !/^(blob:|data:)/.test(href)) return false;
    const name = a.getAttribute('download') || 'liftlog-file';
    const known = blobs.get(a.href);
    const get = known ? Promise.resolve(known) : fetch(a.href).then(r => r.blob());
    get.then(b => shareFile(b, name)).catch((e) => {
      try { cur.app && cur.app.toast && cur.app.toast('Could not save the file'); } catch (_) {}
      console.warn('[native] export failed', e);
    });
    return true;
  };
  const click = HTMLAnchorElement.prototype.click;
  HTMLAnchorElement.prototype.click = function () {
    if (handleDownload(this)) return;
    return click.call(this);
  };
  document.addEventListener('click', (e) => {
    const a = e.target && e.target.closest && e.target.closest('a[download]');
    if (a && handleDownload(a)) e.preventDefault();
  }, true);

  navigator.share = async (data = {}) => {
    try {
      const files = Array.from(data.files || []);
      if (files.length) {
        const uris = [];
        for (const f of files) uris.push(await writeCacheFile(f, f.name || 'liftlog-file'));
        await Share.share({ title: data.title, text: data.text, files: uris, dialogTitle: data.title || 'Share' });
      } else {
        await Share.share({ title: data.title, text: data.text, url: data.url, dialogTitle: data.title || 'Share' });
      }
    } catch (e) {
      if (isCancel(e)) throw abortError();
      throw e;
    }
  };
  navigator.canShare = () => true;

  // Notification shim: lets the existing permission UI and "notify me" menu
  // items work in the app; the rest alert itself is the scheduled native one.
  const N = function (title, opts = {}) {
    if (opts && opts.tag === 'liftlog-rest') return; // scheduled natively already
    notificationStatus().then(p => {
      if (p !== 'granted') return;
      ensureChannels().then(() => LocalNotifications.schedule({
        notifications: [{ id: 41003, title: String(title || 'Lift Log'), body: (opts && opts.body) || '', channelId: 'rest', smallIcon: 'ic_stat_liftlog', iconColor: '#A3E635', autoCancel: true }],
      })).catch(swallow);
    });
  };
  Object.defineProperty(N, 'permission', { get: () => permCache, configurable: true });
  N.requestPermission = (cb) => requestNotifications().then((p) => { try { cb && cb(p); } catch (_) {} return p; });
  window.Notification = N;
  notificationStatus().catch(swallow);
}
let permCache = 'default';
function setPermCache(p) { permCache = p; return p; }

// ───────────────────────────── Health Connect ─────────────────────────────

const HC_KEY = 'liftlog.native.health';
function hcLoad() {
  try { return { on: false, written: {}, ...(JSON.parse(localStorage.getItem(HC_KEY) || '{}')) }; } catch (_) { return { on: false, written: {} }; }
}
function hcSave(v) { try { localStorage.setItem(HC_KEY, JSON.stringify(v)); } catch (_) {} hcEmit(); }
const hcListeners = new Set();
function hcEmit() { hcListeners.forEach(l => { try { l(); } catch (_) {} }); }

/** Health Connect exists on Android only (Apple Health needs the iOS build). */
export function healthSupported() { return isAndroid(); }
export function healthSettings() { return hcLoad(); }
export function useHealthSettings() {
  const [, setN] = React.useState(0);
  React.useEffect(() => { const l = () => setN(n => n + 1); hcListeners.add(l); return () => hcListeners.delete(l); }, []);
  return hcLoad();
}
/** { available, status: 'available' | 'update-required' | 'unavailable' | 'web' } */
export async function healthAvailability() {
  if (!healthSupported()) return { available: false, status: 'web' };
  try { return await LiftHealth.availability(); } catch (_) { return { available: false, status: 'unavailable' }; }
}
/** { available, workouts, weight } — what Health Connect currently allows. */
export async function healthAccess() {
  if (!healthSupported()) return { available: false, workouts: false, weight: false };
  try { return await LiftHealth.checkAccess(); } catch (_) { return { available: false, workouts: false, weight: false }; }
}
/** Opens Health Connect's permission sheet for the chosen data. */
export async function healthRequest({ workouts = true, weight = true } = {}) {
  if (!healthSupported()) return { available: false, workouts: false, weight: false };
  return LiftHealth.requestAccess({ workouts, weight });
}
export async function healthOpenSettings() {
  if (!healthSupported()) return;
  try { await LiftHealth.openSettings(); } catch (_) {}
}
/** Turn the integration on/off on this phone. Turning on records "since" so only new workouts are sent automatically. */
export function setHealthEnabled(on) {
  const v = hcLoad();
  hcSave({ ...v, on: !!on, since: on ? (v.since || Date.now()) : v.since });
}

const lbToKg = (lb) => lb * 0.45359237;
function sessionWindow(s) {
  const start = s.startedAt;
  let end = s.completedAt;
  const lastTs = Math.max(0, ...(s.sets || []).map(x => x.ts || 0));
  // A workout left open for hours: end it a minute after the last logged set.
  if (lastTs > start && end - start > 4 * 3600e3) end = lastTs + 60e3;
  if (!(end > start)) end = start + 60e3;
  return { start, end };
}
function sessionNotes(s, units) {
  const work = (s.sets || []).filter(x => x.type !== 'warmup');
  const vol = work.reduce((t, x) => t + (x.w || 0) * (x.reps || 0), 0);
  const v = units === 'kg' ? lbToKg(vol) : vol;
  const parts = [`${work.length} working set${work.length === 1 ? '' : 's'}`];
  if (v > 0) parts.push(`${Math.round(v).toLocaleString()} ${units === 'kg' ? 'kg' : 'lb'} volume`);
  return parts.join(' · ') + ' · Logged with Lift Log';
}

/** Write one finished workout as a strength-training session. */
export async function healthWriteSession(session, units = 'lb') {
  if (!healthSupported() || !session || !session.completedAt) return null;
  const { start, end } = sessionWindow(session);
  const res = await LiftHealth.writeWorkout({
    id: String(session.id), start, end,
    title: session.name || 'Strength workout',
    notes: sessionNotes(session, units),
  });
  const v = hcLoad();
  hcSave({ ...v, written: { ...v.written, [session.id]: session.completedAt }, lastWrite: Date.now() });
  return res;
}

let hcSyncing = false;
/**
 * Send finished workouts Health Connect has not seen yet (all = include ones
 * finished before the integration was turned on), and remove ones deleted here.
 * → { written, removed, failed }
 */
export async function healthSyncWorkouts(state, { all = false } = {}) {
  const v = hcLoad();
  if (!healthSupported() || !v.on || hcSyncing || !state) return { written: 0, removed: 0 };
  hcSyncing = true;
  let written = 0, removed = 0, failed = false;
  try {
    const access = await healthAccess();
    if (!access.workouts) return { written, removed };
    const units = (state.prefs && state.prefs.units) || 'lb';
    const since = all ? 0 : (v.since || Date.now());
    const todo = (state.sessions || []).filter(s => s.completedAt && (s.sets || []).length && s.completedAt >= since && hcLoad().written[s.id] !== s.completedAt);
    for (const s of todo.slice(0, 200)) {
      try { await healthWriteSession(s, units); written++; } catch (e) { console.warn('[native] Health Connect write failed', e); failed = true; break; }
    }
    // Deleted in Lift Log → delete the copy in Health Connect.
    const deleted = (state.deleted && state.deleted.sessions) || {};
    const w = { ...hcLoad().written };
    for (const id of Object.keys(w)) {
      if (deleted[id] && !(state.sessions || []).some(s => s.id === id)) {
        try { await LiftHealth.deleteWorkout({ id }); delete w[id]; removed++; } catch (_) {}
      }
    }
    if (removed) hcSave({ ...hcLoad(), written: w });
  } finally { hcSyncing = false; }
  return { written, removed, failed };
}

const pad2 = (n) => String(n).padStart(2, '0');
const localDate = (ms) => { const d = new Date(ms); return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; };

/**
 * Read weigh-ins from Health Connect into Body metrics (newest per day; a day
 * already logged in Lift Log is never overwritten). → number imported
 */
export async function healthImportWeight(state, actions, { days = 90 } = {}) {
  const v = hcLoad();
  if (!healthSupported() || !v.on || !state || !actions || !actions.importBodyEntries) return 0;
  const access = await healthAccess();
  if (!access.weight) return 0;
  const since = Math.max(Date.now() - days * 86400e3, (v.weightAt || 0) - 2 * 86400e3);
  const { entries = [] } = await LiftHealth.readWeight({ since, limit: 500 });
  const have = new Set((state.bodyEntries || []).map(b => b.date));
  const byDay = new Map();
  for (const e of entries) {                    // newest first → keep the first per day
    const date = localDate(e.time);
    if (!have.has(date) && !byDay.has(date) && e.kg > 0) byDay.set(date, { date, weight: +(e.kg / 0.45359237).toFixed(2), source: 'health-connect' });
  }
  const list = [...byDay.values()];
  if (list.length) actions.importBodyEntries(list);
  hcSave({ ...hcLoad(), weightAt: Date.now() });
  return list.length;
}

// ───────────────────────────── Wear OS watch ─────────────────────────────
//
// The Lift Log watch app (android/wear) mirrors the live workout and logs sets
// from the wrist. LiftWearPlugin.kt publishes our state as a Wear Data Layer
// item and hands us the watch's commands ('command' events, plus a persisted
// queue for commands that arrived while the WebView was not running). The
// live workout screen registers a link (useWatchLink) so a set logged on the
// watch goes through exactly the same path as a tap on the phone (commitSet:
// rest timer, PR check, focus). Protocol: src/watch_protocol.js.

const LiftWear = registerPlugin('LiftWear');
const WATCH_ACKS_KEY = 'liftlog.watch.acks';
const WATCH_WORKOUT_KEY = 'liftlog.watch.workout';
const watch = {
  link: null,          // ref from useWatchLink (live workout screen), or null
  workout: null,       // last workout snapshot (survives leaving the screen and restarts)
  summary: null,       // next workout + this week, for the tile
  queue: [],           // validated commands waiting to be applied, oldest first
  busy: false,         // a command was applied; wait for the render before the next
  routing: false,      // opened the workout screen to apply a queued command
  key: null,           // identity of what the last snapshot was built from
  sent: '',            // last published JSON without its timestamp
  timer: null,
  acks: (() => { try { const a = JSON.parse(localStorage.getItem(WATCH_ACKS_KEY) || '[]'); return Array.isArray(a) ? a : []; } catch (_) { return []; } })(),
};
try {
  const w = JSON.parse(localStorage.getItem(WATCH_WORKOUT_KEY) || 'null');
  if (w && typeof w === 'object' && w.sessionId) watch.workout = w;
} catch (_) {}

/** Wear OS companion exists on Android only. */
export function watchSupported() { return isAndroid(); }
/** { supported, connected, installed, nodes: [{ id, name, nearby, app }] } — paired watches and whether Lift Log is on them. */
export async function watchStatus() {
  if (!watchSupported()) return { supported: false, connected: false, installed: false, nodes: [] };
  try { return await LiftWear.status(); } catch (_) { return { supported: false, connected: false, installed: false, nodes: [] }; }
}

const liveSessionOf = (st) => (st && (st.sessions || []).find(s => s.id === st.activeSessionId && !s.completedAt)) || null;

// record: list the id in the published acks (the watch drops it from its
// outbox); sync requests are fire and forget, so they are only cleared natively.
function watchAck(id, record = true) {
  if (!id) return;
  if (record && !watch.acks.includes(id)) {
    watch.acks = [...watch.acks, id].slice(-WATCH_LIMITS.maxAcks);
    try { localStorage.setItem(WATCH_ACKS_KEY, JSON.stringify(watch.acks)); } catch (_) {}
  }
  LiftWear.ack({ ids: [id] }).catch(swallow);
  scheduleWatchPublish();
}

function scheduleWatchPublish(force = false) {
  if (!watchSupported()) return;
  if (force) watch.sent = '';
  clearTimeout(watch.timer);
  watch.timer = setTimeout(() => {
    // A watch command was just applied: publish once the screen has re-rendered,
    // so its ack and the logged set reach the watch together.
    if (watch.busy) { scheduleWatchPublish(force); return; }
    const st = cur.state;
    const units = (st && st.prefs && st.prefs.units) || 'lb';
    const live = liveSessionOf(st);
    const workout = live && watch.workout && watch.workout.sessionId === String(live.id) ? withSessionRest(watch.workout, live) : null;
    const msg = buildWatchState({ units, workout, next: watch.summary && watch.summary.next, week: watch.summary && watch.summary.week, acks: watch.acks, now: 0 });
    const key = JSON.stringify(msg);
    if (key === watch.sent) return;
    watch.sent = key;
    LiftWear.publish({ json: JSON.stringify({ ...msg, sentAt: Date.now() }) }).catch(swallow);
  }, 120);
}

function setWatchWorkout(w) {
  watch.workout = w;
  try {
    if (w) localStorage.setItem(WATCH_WORKOUT_KEY, JSON.stringify(w));
    else localStorage.removeItem(WATCH_WORKOUT_KEY);
  } catch (_) {}
  scheduleWatchPublish();
}

function onWatchCommand(raw) {
  const r = parseWatchCommand(raw && raw.json != null ? raw.json : raw);
  if (!r.ok) { if (r.id) watchAck(r.id); console.warn('[native] watch command rejected:', r.error); return; }
  const cmd = r.cmd;
  if (watch.acks.includes(cmd.id)) { LiftWear.ack({ ids: [cmd.id] }).catch(swallow); return; }
  if (watch.queue.some(c => c.id === cmd.id)) return;
  watch.queue.push(cmd);
  watch.queue.sort((a, b) => a.at - b.at);
  pumpWatch();
}

// Apply queued commands one at a time: each one waits for the render of the
// previous one, so the next command sees the updated session.
function pumpWatch() {
  if (watch.busy || !watch.queue.length) return;
  const st = cur.state, app = cur.app;
  if (!st || !app) return;
  const cmd = watch.queue[0];
  const live = liveSessionOf(st);
  const settle = () => { watch.busy = true; setTimeout(() => { if (watch.busy) { watch.busy = false; pumpWatch(); } }, 1500); };
  if (cmd.type === 'sync') { watch.queue.shift(); watchAck(cmd.id, false); scheduleWatchPublish(true); pumpWatch(); return; }
  // A command for a workout that is no longer live: acknowledge and drop it.
  if (!live || String(live.id) !== cmd.sessionId) { watch.queue.shift(); watchAck(cmd.id); pumpWatch(); return; }
  if (cmd.type === 'rest') {
    watch.queue.shift();
    if (live.rest) app.actions.updateSession(live.id, { rest: cmd.action === 'skip' ? null : adjustedRest(live.rest, cmd.seconds) });
    watchAck(cmd.id);
    settle();
    return;
  }
  // Logging and finishing go through the workout screen (same path as a tap).
  const link = watch.link && watch.link.current;
  if (!link || String(link.session.id) !== cmd.sessionId) {
    if (!watch.routing) { watch.routing = true; app.setRoute('active-workout', { dayId: live.dayId || undefined }); }
    return; // the screen registers its link and pumps again
  }
  watch.queue.shift();
  try {
    if (cmd.type === 'log') {
      const slot = link.slots.find(s => s.id === cmd.exId);
      if (slot && cmd.row < slot.rows.length) {
        link.commit(slot, cmd.row, { w: cmd.w, reps: cmd.reps, rpe: null }, { confirmed: true });
        // Delivered late (the phone was out of reach): the rest is long over on the wrist.
        if (Date.now() - cmd.at > 30000) app.actions.updateSession(live.id, { rest: null });
      }
    } else if (cmd.type === 'finish' && (link.session.sets || []).length) {
      link.finish(false);
    }
  } catch (e) { console.warn('[native] watch command failed', e); }
  watchAck(cmd.id);
  settle();
}

async function drainWatchQueue() {
  try {
    const { commands = [] } = await LiftWear.pending();
    for (const json of commands) onWatchCommand({ json });
  } catch (_) {}
}

/**
 * The live workout screen's link to the watch. Call once near the top of the
 * component (before any early return) and fill `.current` on each render with
 * { session, slots, units, prefs, focus, title, sugg, perfFor, kindFor,
 *   commit: commitSet, finish: onFinish }.
 */
export function useWatchLink() {
  const ref = React.useRef(null);
  ref.current = null; // filled by the screen later in this render
  React.useEffect(() => {
    if (!watchSupported()) return undefined;
    watch.link = ref;
    return () => { if (watch.link === ref) watch.link = null; };
  }, []);
  React.useEffect(() => {
    if (!watchSupported()) return;
    const l = ref.current;
    if (!l || !l.session) return;
    watch.routing = false;
    const focusKey = l.focus ? `${l.focus.slotId}:${l.focus.row}` : '';
    const k = watch.key;
    // The screen re-renders every second for its clock: rebuild only on change.
    if (!k || k.session !== l.session || k.slots !== l.slots || k.units !== l.units || k.kindFor !== l.kindFor || k.prefs !== l.prefs || k.focus !== focusKey) {
      watch.key = { session: l.session, slots: l.slots, units: l.units, kindFor: l.kindFor, prefs: l.prefs, focus: focusKey };
      try { setWatchWorkout(buildWatchWorkout(l)); } catch (e) { console.warn('[native] watch snapshot failed', e); }
    }
    if (watch.queue.length && !watch.busy) pumpWatch();
  });
  return ref;
}

// liftlog://watch/start (opened on the phone by the watch): resume the live
// workout, or start the next one in the program.
function onWatchStart() {
  const app = cur.app, st = cur.state;
  if (!app || !st) return;
  const live = liveSessionOf(st);
  if (live) { app.setRoute('active-workout', { dayId: live.dayId || undefined }); return; }
  const program = (st.programs || []).find(p => p.id === st.activeProgramId);
  const nx = program ? nextWorkout(st, program.id) : null;
  if (nx && nx.day) app.setRoute('active-workout', { dayId: nx.day.id });
  else app.setRoute('active-workout', { freestyle: 'Workout' });
}

// ───────────────────────────── app bridge hook ─────────────────────────────

const TAB_ROOTS = new Set(['home', 'progress', 'programs', 'library', 'settings']);

function onBack({ canGoBack } = {}) {
  const app = cur.app;
  if (!app) { CapApp.minimizeApp().catch(swallow); return; }
  // An open sheet owns the top history entry: Back closes it.
  if (openSheetCount() > 0) { try { window.history.back(); } catch (_) {} return; }
  const name = (cur.route && cur.route.name) || 'home';
  if (name === 'home' || name === 'welcome') { CapApp.minimizeApp().catch(swallow); return; }
  if (TAB_ROOTS.has(name) && !canGoBack) { app.replaceRoute('home'); return; }
  app.goBack();
}

function onDeepLink(url) {
  if (!url) return;
  if (/^liftlog:\/\/watch\/start/i.test(url)) { onWatchStart(); return; }
  try {
    // liftlog://<anything>#p=… / #sync=…  or  https://…/#…  → hand the fragment
    // (or query) to the web app exactly as if it had been opened from a link.
    const i = url.indexOf('#');
    const q = url.indexOf('?');
    const frag = i >= 0 ? url.slice(i + 1) : q >= 0 ? url.slice(q + 1) : '';
    if (frag) {
      if (('#' + frag) === window.location.hash) window.dispatchEvent(new HashChangeEvent('hashchange'));
      else window.location.hash = frag;
    }
    window.dispatchEvent(new CustomEvent('liftlog:deeplink', { detail: { url } }));
  } catch (_) {}
}

let inited = false;
function initOnce() {
  if (inited || !isNative()) return;
  inited = true;
  CapApp.addListener('backButton', onBack).catch(swallow);
  CapApp.addListener('appUrlOpen', (e) => onDeepLink(e && e.url)).catch(swallow);
  CapApp.getLaunchUrl().then(r => r && r.url && onDeepLink(r.url)).catch(swallow);
  CapApp.addListener('resume', () => {
    // The rest alert is shown in-app now; clear it from the shade.
    LocalNotifications.removeDeliveredNotifications({ notifications: [{ id: REST_ID, title: '', body: '' }] }).catch(swallow);
    exactCache = null;
    notificationStatus().catch(swallow);
    const st = cur.state, app = cur.app;
    if (st && app && hcLoad().on) {
      healthSyncWorkouts(st).catch(swallow);
      healthImportWeight(st, app.actions).catch(swallow);
    }
  }).catch(swallow);
  // Tapping a notification: reminders open Home, rest alerts the live workout.
  LocalNotifications.addListener('localNotificationActionPerformed', (e) => {
    const extra = (e && e.notification && e.notification.extra) || {};
    const app = cur.app, st = cur.state;
    if (!app || !st) return;
    const live = (st.sessions || []).find(s => s.id === st.activeSessionId && !s.completedAt);
    if ((extra.kind === 'rest' || extra.kind === 'test') && live) app.setRoute('active-workout', { dayId: live.dayId || undefined });
    else if (extra.kind === 'reminder' && extra.route) app.setRoute(extra.route);
  }).catch(swallow);
  document.documentElement.setAttribute('data-ll-native', platform());
  if (watchSupported()) {
    LiftWear.addListener('command', onWatchCommand).catch(swallow);
    CapApp.addListener('resume', () => { scheduleWatchPublish(true); drainWatchQueue(); }).catch(swallow);
    drainWatchQueue();
  }
}

/**
 * Mount once inside AppProvider (shell's ThemeInjector). Safe on the web: there
 * it only keeps the screen awake during a workout (Wake Lock API).
 */
export function useNativeBridge(app) {
  const { state, route } = app;
  cur.state = state; cur.route = route; cur.app = app;
  React.useEffect(() => { initOnce(); }, []);

  // Status bar icons follow the theme (light theme → dark icons).
  const theme = (state.prefs && state.prefs.theme) || 'dark';
  React.useEffect(() => {
    if (!isNative()) return;
    const light = theme === 'light';
    SystemBars.setStyle({ style: light ? SystemBarsStyle.Light : SystemBarsStyle.Dark })
      .catch(() => StatusBar.setStyle({ style: light ? StatusStyle.Light : StatusStyle.Dark }).catch(swallow));
    if (isAndroid()) LiftShell.setBackground({ color: THEME_BG[theme] || THEME_BG.dark }).catch(swallow);
  }, [theme]);

  // Keep the screen on while a workout is live (Settings → Phone & health).
  const live = (state.sessions || []).find(s => s.id === state.activeSessionId && !s.completedAt);
  const liveId = live ? live.id : null;
  const keep = !!liveId && state.prefs.keepAwake !== false;
  React.useEffect(() => {
    setKeepAwake(keep);
    if (!keep || isNative()) return undefined;
    // The web lock drops whenever the tab is hidden: take it again on return.
    const onVis = () => { if (document.visibilityState === 'visible') { wakeOn = false; setKeepAwake(true); } };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [keep]);
  React.useEffect(() => () => { setKeepAwake(false); }, []);

  // Lock-screen rest alert: schedule it with the OS whenever a rest starts or
  // its end moves; cancel when the rest is skipped or the workout ends. A rest
  // that simply ran out (fired) is left alone so the alert is never pulled
  // from the lock screen at the moment it shows.
  const rest = live && live.rest;
  const restLive = !!rest && !rest.fired && rest.endsAt > Date.now();
  const restScheduled = React.useRef(false);
  React.useEffect(() => {
    if (!isNative()) return;
    if (restLive) {
      restScheduled.current = true;
      scheduleRestAlert({ at: rest.endsAt, title: 'Rest over', body: rest.label ? `Next: ${rest.label}` : 'Time for your next set' });
    } else if (restScheduled.current && !(rest && rest.fired)) {
      restScheduled.current = false;
      cancelRestAlert();
    }
  }, [restLive, rest && rest.endsAt, liveId]); // eslint-disable-line react-hooks/exhaustive-deps

  // First workout: ask for notification permission at the start (not mid-set),
  // because the lock-screen rest alert depends on it.
  React.useEffect(() => {
    if (!liveId || !isNative() || state.prefs.notifRest === false) return;
    let asked = false;
    try { asked = localStorage.getItem('liftlog.native.notifAsked') === '1'; } catch (_) {}
    notificationStatus().then(p => {
      if (p !== 'default' || asked) return;
      try { localStorage.setItem('liftlog.native.notifAsked', '1'); } catch (_) {}
      requestNotifications().catch(swallow);
    });
  }, [liveId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Health Connect: send newly finished workouts (debounced; local only).
  const doneSig = (state.sessions || []).reduce((n, s) => n + (s.completedAt ? 1 : 0), 0) + ':' + Object.keys((state.deleted && state.deleted.sessions) || {}).length;
  React.useEffect(() => {
    if (!healthSupported() || !hcLoad().on) return undefined;
    const t = setTimeout(() => { healthSyncWorkouts(cur.state).catch(swallow); }, 1500);
    return () => clearTimeout(t);
  }, [doneSig]);
  React.useEffect(() => {
    if (!healthSupported() || !hcLoad().on) return;
    const t = setTimeout(() => { healthImportWeight(cur.state, cur.app && cur.app.actions).catch(swallow); }, 2500);
    return () => clearTimeout(t);
  }, []);

  // Wear OS watch: keep the tile summary and the live rest current, forget a
  // finished workout, and apply the next queued watch command after a render.
  const units = (state.prefs && state.prefs.units) || 'lb';
  React.useEffect(() => {
    if (!watchSupported()) return;
    try { watch.summary = buildWatchSummary(cur.state); } catch (_) {}
    if (!liveId && watch.workout) setWatchWorkout(null);
    scheduleWatchPublish();
  }, [state.sessions, state.programs, state.activeProgramId, units, liveId]); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (watch.busy) { watch.busy = false; pumpWatch(); }
    else if (watch.queue.length && !watch.routing) pumpWatch();
  });
}

installShims();
